// src/components/bot/hooks/useKeywordManagement.ts
// 🧠 KEYWORD MANAGEMENT LOGIC - Pure business logic
// ✅ FIXED: Event object safety and validation

import { useState, useCallback } from 'react';
import logger from '../../../utils/logger';

interface UseKeywordProps {
  keywords: string[];
  onKeywordsChange: (keywords: string[]) => void;
}

// 🧠 KEYWORD MANAGEMENT HOOK
export const useKeywordManagement = ({ keywords, onKeywordsChange }: UseKeywordProps) => {
  
  // 🎯 KEYWORD INPUT STATE
  const [keywordInput, setKeywordInput] = useState('');

  // 🔥 ADD KEYWORD: Add new keyword from input - ✅ FIXED: Safe parameter handling
  const addKeyword = useCallback((keywordParam?: string) => {
    // ✅ SAFETY: Ensure we never process event objects
    let keywordToAdd: string;
    
    if (typeof keywordParam === 'string') {
      keywordToAdd = keywordParam.trim().toLowerCase();
    } else if (keywordParam === undefined || keywordParam === null) {
      keywordToAdd = keywordInput.trim().toLowerCase();
    } else {
      // ✅ SAFETY: If anything else is passed (like an event object), ignore it
      logger.error('❌ useKeyword: Invalid parameter passed to addKeyword:', typeof keywordParam);
      keywordToAdd = keywordInput.trim().toLowerCase();
    }
    
    if (!keywordToAdd) {
      logger.log('❌ useKeyword: Empty keyword, skipping');
      return false;
    }
    
    // 🎯 CHECK DUPLICATE: Prevent duplicate keywords
    if (keywords.includes(keywordToAdd)) {
      logger.log('❌ useKeyword: Duplicate keyword, skipping:', keywordToAdd);
      return false;
    }
    
    // ✅ FINAL SAFETY: Ensure we're adding a valid string
    if (typeof keywordToAdd === 'string' && keywordToAdd.length > 0) {
      logger.log('➕ useKeyword: Adding keyword:', keywordToAdd);
      onKeywordsChange([...keywords, keywordToAdd]);
      setKeywordInput(''); // Clear input after adding
      return true;
    } else {
      logger.error('❌ useKeyword: Final validation failed for keyword:', keywordToAdd);
      return false;
    }
  }, [keywordInput, keywords, onKeywordsChange]);

  // 🔥 REMOVE KEYWORD: Remove keyword by value - ✅ SAFE: String validation
  const removeKeyword = useCallback((keywordToRemove: string) => {
    // ✅ SAFETY: Ensure we only process strings
    if (typeof keywordToRemove !== 'string') {
      logger.error('❌ useKeyword: Invalid keyword type for removal:', typeof keywordToRemove);
      return;
    }
    
    logger.log('🗑️ useKeyword: Removing keyword:', keywordToRemove);
    
    const filteredKeywords = keywords.filter(k => k !== keywordToRemove);
    onKeywordsChange(filteredKeywords);
  }, [keywords, onKeywordsChange]);

  // 🔥 CLEAR ALL: Remove all keywords
  const clearAllKeywords = useCallback(() => {
    logger.log('🧹 useKeyword: Clearing all keywords');
    onKeywordsChange([]);
  }, [onKeywordsChange]);

  // 🔥 HANDLE INPUT: Update keyword input value - ✅ SAFE: Event validation
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    // ✅ SAFETY: Validate event object
    if (e && e.target && typeof e.target.value === 'string') {
      setKeywordInput(e.target.value);
    } else {
      logger.error('❌ useKeyword: Invalid input change event:', e);
    }
  }, []);

  // 🔥 HANDLE KEY PRESS: Add keyword on Enter or comma - ✅ SAFE: Event validation
  const handleKeyPress = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    // ✅ SAFETY: Validate event object
    if (e && typeof e.key === 'string' && (e.key === 'Enter' || e.key === ',')) {
      e.preventDefault();
      addKeyword();
    }
  }, [addKeyword]);

  // 🔥 KEYWORD VALIDATION: Check if keywords are valid - ✅ SAFE: Array validation
  const validateKeywords = useCallback(() => {
    const errors: Record<string, string> = {};
    
    // ✅ SAFETY: Ensure keywords is an array of strings
    const validKeywords = keywords.filter(k => typeof k === 'string' && k.trim().length > 0);
    
    if (validKeywords.length === 0) {
      errors.keywords = 'At least one keyword is required';
      return { isValid: false, errors };
    }
    
    // 🎯 CHECK LENGTH: Ensure reasonable keyword lengths
    const tooLongKeywords = validKeywords.filter(k => k.length > 50);
    if (tooLongKeywords.length > 0) {
      errors.keywords = 'Keywords must be 50 characters or less';
      return { isValid: false, errors };
    }
    
    // ✅ SAFETY: Filter out any non-string elements
    if (validKeywords.length < keywords.length) {
      logger.warn('⚠️ useKeyword: Found invalid keywords, filtering them out');
      onKeywordsChange(validKeywords);
    }
    
    return { isValid: true, errors: {} };
  }, [keywords, onKeywordsChange]);

  // 🔥 KEYWORD UTILITIES: Helper functions - ✅ SAFE: All validated
  const getKeywordCount = useCallback(() => {
    return keywords.filter(k => typeof k === 'string').length;
  }, [keywords]);

  const hasKeyword = useCallback((keyword: string) => {
    if (typeof keyword !== 'string') return false;
    return keywords.includes(keyword.toLowerCase());
  }, [keywords]);

  const getCurrentInput = useCallback(() => {
    return keywordInput;
  }, [keywordInput]);

  const canAddFromInput = useCallback(() => {
    const trimmed = keywordInput.trim().toLowerCase();
    return trimmed.length > 0 && !keywords.includes(trimmed);
  }, [keywordInput, keywords]);

  // 🔥 BULK OPERATIONS: Handle multiple keywords - ✅ SAFE: Array validation
  const addMultipleKeywords = useCallback((newKeywords: string[]) => {
    // ✅ SAFETY: Validate input is array of strings
    if (!Array.isArray(newKeywords)) {
      logger.error('❌ useKeyword: addMultipleKeywords expects array, got:', typeof newKeywords);
      return 0;
    }
    
    const validKeywords = newKeywords
      .filter(k => typeof k === 'string')
      .map(k => k.trim().toLowerCase())
      .filter(k => k.length > 0 && !keywords.includes(k));
    
    if (validKeywords.length > 0) {
      logger.log('➕ useKeyword: Adding multiple keywords:', validKeywords);
      onKeywordsChange([...keywords, ...validKeywords]);
    }
    
    return validKeywords.length;
  }, [keywords, onKeywordsChange]);

  const parseKeywordsFromText = useCallback((text: string) => {
    // ✅ SAFETY: Validate input is string
    if (typeof text !== 'string') {
      logger.error('❌ useKeyword: parseKeywordsFromText expects string, got:', typeof text);
      return [];
    }
    
    // 🎯 PARSE: Split by comma, newline, or semicolon
    return text
      .split(/[,\n;]/)
      .map(k => k.trim().toLowerCase())
      .filter(k => k.length > 0);
  }, []);

  // 🔥 KEYWORD STATS: Computed values - ✅ SAFE: All validated
  const keywordStats = {
    total: keywords.filter(k => typeof k === 'string').length,
    averageLength: keywords.length > 0 
      ? Math.round(keywords
          .filter(k => typeof k === 'string')
          .reduce((sum, k) => sum + k.length, 0) / keywords.filter(k => typeof k === 'string').length)
      : 0,
    longest: keywords.length > 0 
      ? Math.max(...keywords.filter(k => typeof k === 'string').map(k => k.length))
      : 0,
    shortest: keywords.length > 0 
      ? Math.min(...keywords.filter(k => typeof k === 'string').map(k => k.length))
      : 0
  };

  // 🔥 RETURN: Clean interface for UI components
  return {
    // State
    keywordInput,
    keywords: keywords.filter(k => typeof k === 'string'), // ✅ SAFETY: Only return strings
    
    // Input handlers
    handleInputChange,
    handleKeyPress,
    setKeywordInput,
    
    // Core actions
    addKeyword,
    removeKeyword,
    clearAllKeywords,
    
    // Bulk operations
    addMultipleKeywords,
    parseKeywordsFromText,
    
    // Validation
    validateKeywords,
    
    // Utilities
    getKeywordCount,
    hasKeyword,
    getCurrentInput,
    canAddFromInput,
    
    // Stats
    keywordStats
  };
};